import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react"

const responsive = (breakPoints) => {
  if (!breakPoints) return

  const sortedBreakPoints =
    breakPoints.sort((a, b) => {
      if (a.point >= b.point) return -1

      return 1
    })

  for (let breakPoint of sortedBreakPoints) {
    if (window.innerWidth >= breakPoint.point)
      return breakPoint
  }

  return breakPoints[breakPoints.length - 1]
}

const VirtualGrid = (props) => {
  const {
    next,
    data,
    className,
    hasMore,
    offset = 1,
    rowHeight,
    renderItem,
    sameHeightAndWidth = false,
    dataLength = 0,
    breakPoints,
    listHeaderComponent,
    listFooterComponent,
    numberOfColumns = 1,
    listEmptyComponent = null,
    loader = null
  } = props

  const breakPoint = responsive(breakPoints)

  const initialColumnsState =
    breakPoint?.numberOfColumns ||
    numberOfColumns

  const initialHeightState =
    rowHeight ||
    breakPoint?.rowHeight

  const [columns, setColumns] = useState(initialColumnsState)
  const [itemHeight, setItemHeight] = useState(initialHeightState)
  const [rowPositions, setRowPosition] = useState({ startRow: 0, endRow: 0 })

  const containerRef = useRef(null)
  const wrapperRef = useRef(null)
  const previousStartRow = useRef()
  const previousDataLength = useRef()

  const handleResize =
    useCallback(() => {
      if (!containerRef.current) return

      if (!wrapperRef.current) return

      previousStartRow.current = undefined

      showViewableItems()

      listenToScrollProgress()

      const breakPoint = responsive(breakPoints)

      const columns =
        breakPoint?.numberOfColumns ||
        numberOfColumns

      const height =
        sameHeightAndWidth
          ? wrapperRef.current.clientWidth / columns
          : (breakPoint?.rowHeight || rowHeight)

      setColumns(columns)

      setItemHeight(height)
    }, [
      data,
      next,
      hasMore,
      dataLength,
      columns,
      itemHeight
    ])

  const showViewableItems =
    useCallback(() => {
      if (data.length === 0) return

      const top =
        containerRef.current.scrollTop -
        (wrapperRef.current.offsetTop - containerRef.current.offsetTop)

      let startRow =
        Math.max(((top / itemHeight) | 0), 0)

      const viewPortHeight =
        containerRef.current.clientHeight

      const totalRows = Math.ceil(data.length / columns)

      if (previousStartRow.current === startRow) return
      else previousStartRow.current = startRow

      const numberOfRows = Math.ceil(viewPortHeight / itemHeight)

      const endRow =
        Math.min(startRow + numberOfRows + offset, totalRows)

      startRow =
        Math.max(startRow - offset, 0)

      setRowPosition({
        startRow,
        endRow
      })
    }, [
      data,
      columns,
      itemHeight
    ])

  const listenToScrollProgress =
    useCallback(() => {
      if (!next) return

      if (!hasMore) return

      if (data.length === 0) return

      const winScroll = containerRef.current.scrollTop

      const height =
        containerRef.current.scrollHeight -
        containerRef.current.clientHeight

      if (winScroll + 100 < height) return

      if (previousDataLength.current === dataLength) return

      previousDataLength.current = dataLength

      next()
    }, [
      data,
      next,
      hasMore,
      dataLength,
      columns,
      itemHeight
    ])

  const handleScroll =
    useCallback(() => {
      if (!wrapperRef.current) return

      showViewableItems()

      listenToScrollProgress()

      const breakPoint = responsive(breakPoints)

      const columns =
        breakPoint?.numberOfColumns ||
        numberOfColumns

      const height =
        sameHeightAndWidth
          ? wrapperRef.current.clientWidth / columns
          : (breakPoint?.rowHeight || rowHeight)

      setColumns(columns)

      setItemHeight(height)
    }, [
      data,
      next,
      hasMore,
      dataLength,
      columns,
      itemHeight
    ])

  const initialize = () => {
    previousStartRow.current = undefined

    showViewableItems()

    setTimeout(() => {
      listenToScrollProgress()
    }, 5)
  }

  useLayoutEffect(() => {
    initialize()
  }, [data, columns, itemHeight])

  useLayoutEffect(() => {
    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(handleResize)

      containerRef.current &&
        observer.observe(containerRef.current)

      containerRef.current.addEventListener("scroll", handleScroll)

      return () => {
        containerRef.current &&
          observer.unobserve(containerRef.current)

        containerRef.current.removeEventListener("scroll", handleScroll)
      }
    } else {
      handleResize()

      window.addEventListener("resize", handleResize)

      containerRef.current.addEventListener("scroll", handleScroll)

      return () => {
        window.removeEventListener("resize", handleResize)

        containerRef.current &&
          containerRef.current.removeEventListener("scroll", handleScroll)
      }
    }
  }, [
    data,
    columns,
    dataLength,
    hasMore,
    itemHeight
  ])

  const totalRows =
    Math.ceil(data.length / columns)

  const paddingTop =
    `${rowPositions.startRow * itemHeight}px`

  const paddingBottom =
    `${(totalRows - rowPositions.endRow) * itemHeight}px`

  const items =
    useMemo(() => (
      data.slice(
        rowPositions.startRow * columns,
        rowPositions.endRow * columns
      )
    ), [
      columns,
      rowPositions.startRow,
      rowPositions.endRow
    ])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ overflow: "auto" }}
    >
      {listHeaderComponent}
      {data.length !== 0
        ?
        <div
          ref={wrapperRef}
          style={{
            display: "flex",
            flexWrap: "wrap",
            paddingTop,
            paddingBottom
          }}
        >
          {items.map((item, index) =>
            <div
              key={item._id}
              style={{
                width: `calc(100% / ${columns})`,
                height: `${itemHeight}px`
              }}
            >
              {renderItem(item, index)}
            </div>
          )}
        </div>
        :
        listEmptyComponent
      }
      {hasMore && loader}
      {listFooterComponent}
    </div>
  )
}

export default VirtualGrid